import React, { useRef, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { useEditor } from '../stores-v2/StoreContext';
import { TextElement, VideoElement } from './models';
import { ImageElement } from './models/elements/ImageElement';
import { AudioElement } from './models/elements/AudioElement';
import { Renderer } from '../renderer/Renderer';
import { EditorDemoStore } from './EditorDemoStore';
import { SimplePlaybackControls } from './components/SimplePlaybackControls';
import { PlaybackControls } from './components/PlaybackControls';
import { Timeline } from './components/Timeline';
import TranscriptPanel from './components/TranscriptPanel';
import ReplaceWordModal from './components/ReplaceWordModal';
import PhotoSelectionModal from './components/PhotoSelectionModal';
import { runInAction } from 'mobx';
import { EditorChangeType } from './types';
import { VideoEditor } from './VideoEditor';
import { createContext, useContext } from 'react';
import { useVideoEditorLogging } from '../hooks/useVideoEditorLogging';

// Create DemoStore context
const DemoStoreContext = createContext<EditorDemoStore | null>(null);

// Hook to use demo store
export const useDemoStore = () => {
  const demoStore = useContext(DemoStoreContext);
  if (!demoStore) {
    throw new Error(
      'useDemoStore must be used within a DemoStoreContext.Provider',
    );
  }
  return demoStore;
};

/**
 * Demo component to show the editor in action
 */
const EditorDemo = observer(() => {
  const editorStore = useEditor();
  const rendererRef = useRef<HTMLDivElement>(null);
  const [demoStore] = useState(() => new EditorDemoStore(editorStore));

  // Set up editor logging for the demo
  const isDevMode = process.env.NODE_ENV === 'development';
  useVideoEditorLogging(
    editorStore?.editor,
    {
      enableCommandLogging: true,
      enableTimelineLogging: isDevMode,
      enableTranscriptLogging: isDevMode,
      enableBatchLogging: true,
      enablePerformanceLogging: isDevMode,
      detailedCommandPerformance: isDevMode,
    },
    true, // Always enable in demo
  );

  // Initialize the renderer with all necessary callbacks
  useEffect(() => {
    if (rendererRef.current && !demoStore.renderer) {
      // Create a new renderer instance
      const newRenderer = new Renderer(
        rendererRef.current,
        'interactive',
        'public-juuabbc8amz25dcfhribv3ss', // API key
      );

      // Set up event handlers
      newRenderer.onReady = async () => {
        console.log('Renderer ready');

        // Set up renderer options
        await newRenderer.setZoom('auto');
        await newRenderer.setLoop(false);

        // Save the renderer reference in demoStore first
        demoStore.setRenderer(newRenderer);

        // IMPORTANT: Connect the editor to the renderer AFTER setting in demoStore
        // This ensures that both stores reference the same renderer instance
        editorStore.editor.connectRenderer(newRenderer);

        // Register MobX reactivity observer for Demo
        const mobxReactivityObserver = {
          onEditorChange: (
            editor: VideoEditor,
            changeType: EditorChangeType,
            payload: any,
          ) => {
            if (changeType === EditorChangeType.TIMELINE_CHANGED) {
              editorStore.refreshTimeline();
            } else if (
              changeType === EditorChangeType.ACTIVE_TRANSCRIPT_CHANGED
            ) {
              console.log(
                'Received ACTIVE_TRANSCRIPT_CHANGED event with payload:',
                {
                  hasActiveTranscript: !!payload?.activeTranscript,
                  activeTranscriptLength:
                    payload?.activeTranscript?.length || 0,
                },
              );

              // Pass the entire payload to refreshTranscript
              editorStore.refreshTranscript(payload);
            } else if (changeType === EditorChangeType.SELECTION_CHANGED) {
              // No need to do anything special - MobX will handle the reactivity
              // since we're updating the observable selectedElementIds property
              console.log('Selection changed:', payload?.selectedElementIds);
            } else if (changeType === EditorChangeType.TRANSCRIPT_LOADED) {
              console.log('Transcript loaded');
            }
          },
        };

        editorStore.editor.registerObserver(mobxReactivityObserver, [
          EditorChangeType.TIMELINE_CHANGED,
          EditorChangeType.ACTIVE_TRANSCRIPT_CHANGED,
          EditorChangeType.SELECTION_CHANGED,
        ]);

        // Initialize the observer pattern to ensure changes are properly monitored
        editorStore.editor.initializeBuiltInObservers();

        runInAction(() => {
          editorStore.isReady = true;
        });
      };

      // Track loading state to minimize console noise
      let isLoading = false;

      newRenderer.onLoad = () => {
        if (!isLoading) {
          console.log('Renderer loading');
          isLoading = true;
        }
      };

      newRenderer.onLoadComplete = () => {
        if (isLoading) {
          console.log('Renderer load complete');
          isLoading = false;
        }
      };

      newRenderer.onPlay = () => {
        console.log('Playback started');
        demoStore.isPlaying = true;
      };

      newRenderer.onPause = () => {
        console.log('Playback paused');
        demoStore.isPlaying = false;
      };

      newRenderer.onTimeChange = (time) => {
        if (!demoStore.isPlayheadDragging) {
          // Use the new rAF‑batched update method to keep UI performant.
          demoStore.scheduleTimeUpdate(time);
        }
      };

      newRenderer.onActiveElementsChange = (elementIds) => {
        console.log('Active elements changed:', elementIds);
        if (elementIds.length > 0) {
          editorStore.selectElement(elementIds[0]);
          // Force MobX to recognize the change
          editorStore.refreshTimeline();
        } else {
          editorStore.clearSelection();
        }
      };

      newRenderer.onStateChange = (state) => {
        console.log('Renderer state changed');
        // The editor handles this internally, no need to do anything here
      };
    }
  }, [editorStore, demoStore]);

  // Create a sample project with proper Creatomate timing properties
  const createSampleProject = React.useCallback(() => {
    // Check if tracks already exist to avoid duplicates
    if (editorStore.timeline.tracks.length > 0) {
      console.log('Tracks already exist, skipping sample project creation');
      return;
    }

    console.log('Creating sample project with Creatomate specifications');

    // Create tracks for different media types
    const videoTrack = editorStore.addTrack('video');
    const imageTrack = editorStore.addTrack('image');
    const textTrack = editorStore.addTrack('text');
    const audioTrack = editorStore.addTrack('audio');

    // Batch operations to avoid multiple renderer syncs
    // First, prepare all elements

    // Main background video (with trim_start showing proper video trimming)
    const mainVideo = new VideoElement({
      time: 0,
      duration: 10,
      source:
        'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4',
      track: Number(videoTrack.id),
      // trim_start: 5, // Start 5 seconds into the source video
      // trim_duration: 8, // Use 8 seconds of the video after trim_start
      volume: '50%', // Lower volume to demonstrate proper volume handling
    });

    // Background audio track with 'media' duration to demonstrate this special value
    const backgroundAudio = new AudioElement({
      time: 0,
      duration: 8,
      source:
        'https://actions.google.com/sounds/v1/sports/bowling_gutter_ball_return.ogg', // This URL has CORS enabled
      track: Number(audioTrack.id),
      volume: '20%', // Lower volume for background music
      audio_fade_in: 1.5, // Fade in over 1.5 seconds
      audio_fade_out: 2, // Fade out over 2 seconds
    });

    // B-Roll video clip (showing different trim parameters)
    // const brollVideo = new VideoElement({
    //   time: 10, // Appears after 6 seconds in the timeline
    //   duration: 4, // Lasts for 4 seconds
    //   source:
    //     'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
    //   track: Number(videoTrack.id),
    //   trim_start: 10, // Start 10 seconds into the source video
    //   trim_duration: 4, // Only use 4 seconds of the video
    //   // The following properties demonstrate positioning and sizing
    //   x: '75%', // Position to the right side
    //   y: '25%', // Position to the top
    //   width: '40%', // Smaller width
    //   height: '40%', // Smaller height
    //   border_radius: '0.5 vmin', // Rounded corners
    // });

    // Image with proper positioning and scaling
    const imageElement = new ImageElement({
      time: 2, // Appears after 2 seconds
      duration: 5,
      // Use a real image URL that will display in the timeline
      source: 'https://picsum.photos/800/450',
      track: Number(imageTrack.id),
      x: '50%', // Center horizontally
      y: '50%', // Center vertically
      width: '50%', // Medium width (scaled correctly)
      height: 'auto', // Auto height to maintain aspect ratio
      fit: 'contain', // Ensure the image maintains its aspect ratio
    });

    // Title text
    const titleText = new TextElement({
      time: 1, // Appears after 1 second
      duration: 4,
      text: 'Video Editor Demo',
      font_size: 40,
      fill_color: '#FFFFFF',
      track: Number(textTrack.id),
      // Center top positioning
      x: '50%',
      y: '15%',
      // Text formatting
      font_family: 'Arial',
      font_weight: 700, // Bold
    });

    // Subtitle text
    const subtitleText = new TextElement({
      text: 'With Creatomate Integration',
      // font_size: 24,
      // fill_color: '#FFD700', // Gold color
      // track: Number(textTrack.id),
      // // Center positioning below title
      x: '50%',
      y: '100%',
      time: 5.5, // Appears after 7 seconds
      duration: 1,
      font_size: 32,
      fill_color: '#00FFFF', // Cyan color
      track: Number(textTrack.id),
      // Center bottom positioning
      // x: '50%',
      // y: '85%',
    });

    // Closing text
    const closingText = new TextElement({
      time: 7, // Appears after 7 seconds
      duration: 3,
      text: 'Thank you for watching!',
      font_size: 32,
      fill_color: '#00FFFF', // Cyan color
      track: Number(textTrack.id),
      // Center bottom positioning
      x: '50%',
      y: '85%',
    });

    // Now add all elements to their tracks with sync=false to batch updates
    // Only sync after the last element is added
    editorStore.addElementToTrack(videoTrack.id, mainVideo);
    // editorStore.addElementToTrack(videoTrack.id, brollVideo);
    editorStore.addElementToTrack(imageTrack.id, imageElement);
    editorStore.addElementToTrack(textTrack.id, titleText);
    editorStore.addElementToTrack(textTrack.id, subtitleText);
    editorStore.addElementToTrack(textTrack.id, closingText);
    editorStore.addElementToTrack(audioTrack.id, backgroundAudio); // Only sync on the last element

    demoStore.loadSampleTranscript(videoTrack.id);

    console.log('Sample project created with Creatomate timing specifications');
  }, [editorStore]);

  // Create a sample project when the editor is ready
  useEffect(() => {
    if (editorStore.isReady) {
      createSampleProject();
    }
  }, [editorStore.isReady, createSampleProject, editorStore]);

  // Handle selection with support for multi-select
  const handleElementSelect = (
    elementId: string,
    isMultiSelect: boolean = false,
  ) => {
    console.log(
      `Element selected: ${elementId}, multiSelect: ${isMultiSelect}`,
    );

    if (isMultiSelect) {
      // Toggle selection for multi-select (add or remove from selection)
      if (editorStore.selectedElementIds.includes(elementId)) {
        editorStore.deselectElement(elementId);
      } else {
        editorStore.addElementToSelection(elementId);
      }
    } else {
      // Single selection (replaces current selection)
      editorStore.selectElement(elementId);
    }

    // Force a refresh to ensure the InfoSection updates
    if (editorStore.selectedElementIds.length > 0) {
      console.log(
        'Selected elements:',
        editorStore.selectedElements.map((e) => e.id),
      );
    }
  };

  // Handle opening the replace word modal
  const handleReplaceWord = (wordId: string, wordText: string) => {
    demoStore.openReplaceWordModal(wordId, wordText);
  };

  return (
    <DemoStoreContext.Provider value={demoStore}>
      <EditorContainer>
        <Header>
          <h1>Video Editor Demo</h1>
        </Header>

        <MainContent>
          <EditorUpperSection>
            {/* Transcript Panel (left side) */}
            <TranscriptSection>
              <TranscriptPanel
                originalTranscript={editorStore.editor.getOriginalTranscript()}
                activeTranscript={editorStore.currentActiveTranscript}
                onWordClick={(elementId, timelineTs) => {
                  // Jump to the timestamp for playhead positioning
                  demoStore.jumpToTranscriptElement(elementId, timelineTs);

                  // Set cursor position for operations like paste
                  if (elementId) {
                    demoStore.setCursorPosition(parseInt(elementId));
                  }
                }}
                onRemoveSegment={(segments) => {
                  console.log('Remove segments requested:', segments);
                  // Use the updated commandRemoveTranscriptSegment method with segments array
                  editorStore.editor.commandRemoveTranscriptSegment(segments);
                }}
                onRestoreSegment={(wordIds) => {
                  console.log('Restore - TO DO');
                }}
                onReplaceWord={handleReplaceWord}
                onAttachPhoto={(wordIds) => {
                  console.log('Attach photo requested for words:', wordIds);
                  demoStore.openPhotoSelectionModal(wordIds);
                }}
                onCutWords={(segments) => {
                  console.log('Cut segments requested:', segments);
                  editorStore.editor.commandCutTranscriptSegment(segments);
                }}
                onPasteWords={(time, startInitialIndex, endInitialIndex) => {
                  console.log(
                    `Paste words requested at time ${time}s with range ${startInitialIndex}-${endInitialIndex}`,
                  );
                  demoStore.pasteTranscriptWords(
                    time,
                    startInitialIndex,
                    endInitialIndex,
                  );
                }}
                hasCutWords={demoStore.hasCutTranscriptWords()}
              />

              {/* Transcript controls */}
              <TranscriptControls>
                <PropertyButton
                  onClick={() => {
                    editorStore.editor.undo();
                  }}
                >
                  Undo
                </PropertyButton>

                <PropertyButton
                  onClick={() => {
                    editorStore.editor.redo();
                  }}
                >
                  Redo
                </PropertyButton>
              </TranscriptControls>
            </TranscriptSection>

            {/* Player Section (right side) */}
            <PlayerSection>
              {/* Simple Playback controls */}
              <SimplePlaybackControls
                demoStore={demoStore}
                duration={editorStore.timeline.duration || 10}
              />

              {/* Full Playback controls */}
              <PlaybackControls
                demoStore={demoStore}
                duration={editorStore.timeline.duration || 10}
              />

              {/* Renderer container */}
              <PlayerContainer ref={rendererRef} />
            </PlayerSection>
          </EditorUpperSection>

          {/* Timeline and tracks (full width under the upper section) */}
          <TimelineSection>
            <Timeline
              demoStore={demoStore}
              editorStore={editorStore}
              onElementSelect={handleElementSelect}
            />
          </TimelineSection>

          <InfoSection>
            <PropertiesPanel>
              {editorStore.selectedElementIds.length > 0 &&
              editorStore.selectedElements.length > 0 ? (
                <>
                  <h3>
                    Selected Elements ({editorStore.selectedElementIds.length})
                  </h3>

                  <ElementsList>
                    {editorStore.selectedElements.map((element) => (
                      <ElementItem
                        key={element.id}
                        isSelected={true}
                        onClick={(e) => {
                          // Allow Shift+click to remove from selection
                          if (e.shiftKey) {
                            handleElementSelect(element.id, true);
                          }
                        }}
                      >
                        <ElementTypeLabel>{element.type}</ElementTypeLabel>
                        <ElementTimingInfo>
                          Start: {element.time.toFixed(2)}s, Duration:{' '}
                          {element.durationSeconds.toFixed(2)}s
                        </ElementTimingInfo>
                        {element.type === 'text' && (
                          <ElementTextContent>
                            {(element as TextElement).text}
                          </ElementTextContent>
                        )}
                      </ElementItem>
                    ))}
                  </ElementsList>

                  <PropertyControls>
                    <PropertyButton
                      onClick={() => {
                        // Apply to all selected elements using the command pattern
                        editorStore.selectedElements.forEach((element) => {
                          // Use moveElement command with direct element reference
                          editorStore.moveElement(
                            element.id,
                            element.time + 0.5,
                          );
                        });
                      }}
                    >
                      Move +0.5s
                    </PropertyButton>

                    {/* <PropertyButton
                    onClick={() => {
                      // Apply to all selected elements using the command pattern
                      editorStore.selectedElements.forEach((element) => {
                        const currentEndTime =
                          element.time +
                          (typeof element.duration === 'number'
                            ? element.duration
                            : 0);
                        // Use extendElement command
                        editorStore.extendElement(
                          element.id,
                          currentEndTime + 1,
                        );
                      });
                    }}
                  >
                    Extend Duration +1s
                  </PropertyButton> */}

                    <PropertyButton
                      onClick={() => {
                        // Apply to all selected elements using command pattern
                        editorStore.selectedElements.forEach((element) => {
                          // Use trimElementStart command with direct element reference
                          editorStore.trimElementStart(
                            element.id,
                            element.time + 0.5,
                          );
                        });
                      }}
                    >
                      Trim Start +0.5s
                    </PropertyButton>

                    <PropertyButton
                      onClick={() => {
                        // Apply to all selected elements using command pattern
                        editorStore.selectedElements.forEach((element) => {
                          const currentEndTime =
                            element.time + element.durationSeconds;
                          // Use trimElementEnd command with direct element reference
                          editorStore.trimElementEnd(
                            element.id,
                            currentEndTime - 0.5,
                          );
                        });
                      }}
                    >
                      Trim End -0.5s
                    </PropertyButton>

                    <PropertyButton
                      onClick={() => {
                        // Apply to all selected elements
                        const elementsToRemove = [
                          ...editorStore.selectedElements,
                        ];
                        editorStore.clearSelection();

                        // Remove each element using direct element reference
                        elementsToRemove.forEach((element) => {
                          editorStore.removeElement(element.id);
                        });
                      }}
                      danger
                    >
                      Remove Selected
                    </PropertyButton>
                  </PropertyControls>

                  {editorStore.selectedElements.length === 1 && (
                    <PropertyJson>
                      <h4>Element Properties</h4>
                      <pre>
                        {JSON.stringify(
                          editorStore.selectedElements[0],
                          null,
                          2,
                        )}
                      </pre>
                    </PropertyJson>
                  )}
                </>
              ) : (
                <EmptySelection>
                  <h3>No elements selected</h3>
                  <p>Click on an element in the timeline to select it.</p>
                  <p>Hold Shift while clicking to select multiple elements.</p>
                  <pre style={{ fontSize: '11px', color: '#666' }}>
                    Selected IDs:{' '}
                    {JSON.stringify(editorStore.selectedElementIds)}
                  </pre>

                  {/* Debug buttons for selection issues */}
                  <div
                    style={{
                      marginTop: '10px',
                      display: 'flex',
                      gap: '8px',
                      justifyContent: 'center',
                    }}
                  >
                    <button
                      onClick={() => {
                        // Force selection of the first element as a test
                        const firstElement =
                          editorStore.timeline.allElements[0];
                        if (firstElement) {
                          console.log(
                            `Forcing selection of element: ${firstElement.id}`,
                          );
                          editorStore.selectElement(firstElement.id);
                        }
                      }}
                      style={{
                        padding: '4px 8px',
                        background: '#333',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                      }}
                    >
                      Select First Element
                    </button>

                    <button
                      onClick={() => {
                        console.log('Clearing selection');
                        editorStore.clearSelection();
                      }}
                      style={{
                        padding: '4px 8px',
                        background: '#333',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                      }}
                    >
                      Clear Selection
                    </button>
                  </div>
                </EmptySelection>
              )}
            </PropertiesPanel>
          </InfoSection>
        </MainContent>

        {/* Replace Word Modal */}
        <ReplaceWordModal />

        {/* Photo Selection Modal */}
        <PhotoSelectionModal />
      </EditorContainer>
    </DemoStoreContext.Provider>
  );
});

// Styled components
const EditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  color: #fff;
  background-color: #030419;
`;

const Header = styled.header`
  padding: 16px;
  background-color: #171724;
  border-bottom: 1px solid #333;

  h1 {
    margin: 0;
    font-size: 24px;
  }
`;

const MainContent = styled.main`
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 16px;
  gap: 16px;
  overflow: auto;
`;

const EditorUpperSection = styled.div`
  display: flex;
  width: 100%;
  gap: 16px;
  margin-bottom: 16px;
`;

const TranscriptSection = styled.section`
  width: 25%;
  display: flex;
  flex-direction: column;
`;

const PlayerSection = styled.section`
  display: flex;
  flex-direction: column;
  width: 75%;
`;

const PlayerContainer = styled.div`
  width: 100%;
  height: 400px;
  background-color: #000;
  border-radius: 4px;
`;

const TimelineSection = styled.section`
  width: 100%;
`;

const InfoSection = styled.section`
  display: flex;
  gap: 16px;
  width: 100%;

  @media (max-width: 1200px) {
    flex-direction: column;
  }
`;

const ElementsPanel = styled.div`
  flex: 1;
  background-color: #171724;
  border-radius: 4px;
  padding: 16px;

  h3 {
    margin-top: 0;
  }
`;

const ElementsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: auto;
`;

const ElementItem = styled.div<{ isSelected: boolean }>`
  padding: 8px;
  border-radius: 4px;
  background-color: ${(props) => (props.isSelected ? '#1E3A8A' : '#333')};
  cursor: pointer;

  &:hover {
    background-color: ${(props) => (props.isSelected ? '#1E3A8A' : '#444')};
  }
`;

const ElementTypeLabel = styled.div`
  font-weight: bold;
  margin-bottom: 4px;
`;

const ElementTimingInfo = styled.div`
  font-size: 12px;
  color: #ccc;
  margin-bottom: 4px;
`;

const ElementTextContent = styled.div`
  font-style: italic;
  color: #fff;
  margin-top: 4px;
`;

const PropertiesPanel = styled.div`
  flex: 1;
  background-color: #171724;
  border-radius: 4px;
  padding: 16px;

  h3 {
    margin-top: 0;
  }
`;

const TranscriptControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
  padding: 12px;
  background-color: #171724;
  border-radius: 4px;
`;

const PropertyControls = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const PropertyButton = styled.button<{ danger?: boolean }>`
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  background-color: ${(props) => (props.danger ? '#FF3D71' : '#006FEE')};
  color: #fff;
  cursor: pointer;

  &:hover {
    background-color: ${(props) => (props.danger ? '#E01E5A' : '#0053D9')};
  }
`;

const PropertyJson = styled.div`
  background-color: #0f0f1a;
  border-radius: 4px;
  padding: 8px;
  margin-top: 16px;
  max-height: 300px;
  overflow: auto;

  h4 {
    margin-top: 0;
    margin-bottom: 8px;
    font-size: 14px;
    color: #fff;
  }

  pre {
    margin: 0;
    font-family: monospace;
    font-size: 12px;
  }
`;

const EmptySelection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #666;
  text-align: center;

  h3 {
    margin-top: 0;
    color: #999;
  }

  p {
    margin: 4px 0;
    font-size: 14px;
  }
`;

export default EditorDemo;
